import { useState } from 'react';
import { api, type LineItem } from '../lib/api';
import { ErrorNote, Money, Sheet } from '../components/ui';

/**
 * Edits one per-diem day, which in practice means "it turns out lunch was
 * provided that day".
 *
 * The day type is not editable here: it follows from the trip's dates, so
 * changing it means regenerating the days rather than patching one row. The
 * amount is recomputed server-side from the components frozen on this row, so
 * a correction made months later cannot re-price the day at today's rate.
 */
export function PerDiemDaySheet({
  open, onClose, line, onSaved,
}: {
  open: boolean;
  onClose: () => void;
  line: LineItem | null;
  onSaved: () => void;
}) {
  const [claims, setClaims] = useState({
    breakfastClaimed: true, lunchClaimed: true, dinnerClaimed: true, incidentalsClaimed: true,
  });
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  // Seed from the line the first time this particular day is opened.
  if (line && loadedFor !== line.id) {
    setClaims({
      breakfastClaimed: line.breakfast_claimed ?? true,
      lunchClaimed: line.lunch_claimed ?? true,
      dinnerClaimed: line.dinner_claimed ?? true,
      incidentalsClaimed: line.incidentals_claimed ?? true,
    });
    setNote('');
    setConfirmingDelete(false);
    setLoadedFor(line.id);
  }

  if (!line) return null;

  const components = {
    breakfast: Number(line.breakfast_amount ?? 0),
    lunch: Number(line.lunch_amount ?? 0),
    dinner: Number(line.dinner_amount ?? 0),
    incidentals: Number(line.incidentals_amount ?? 0),
  };
  const percent = Number(line.proration_percent ?? 1);

  // Mirrors the server: meals leave at full value, then the day's percent.
  const subtotal =
    (claims.breakfastClaimed ? components.breakfast : 0) +
    (claims.lunchClaimed ? components.lunch : 0) +
    (claims.dinnerClaimed ? components.dinner : 0) +
    (claims.incidentalsClaimed ? components.incidentals : 0);
  const preview = Math.round(subtotal * percent * 100) / 100;

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/line-items/${line!.id}/per-diem`, {
        ...claims,
        ...(note ? { mealsProvidedNote: note } : {}),
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update that day');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await api.delete(`/line-items/${line!.id}`);
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove that day');
    } finally {
      setBusy(false);
      setConfirmingDelete(false);
    }
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={`Per diem — ${line.entry_date ?? line.expense_date}`}
      footer={
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600">This day</span>
            <Money value={preview} className="text-lg font-semibold" />
          </div>
          <button type="button" className="btn-primary w-full" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          {confirmingDelete ? (
            <div className="flex gap-2">
              <button type="button" className="btn-secondary flex-1" disabled={busy}
                      onClick={() => setConfirmingDelete(false)}>Keep it</button>
              <button type="button" className="btn-danger flex-1" disabled={busy}
                      onClick={() => void remove()}>{busy ? 'Removing…' : 'Yes, remove'}</button>
            </div>
          ) : (
            <button type="button" className="btn-danger w-full" disabled={busy}
                    onClick={() => setConfirmingDelete(true)}>Remove this day</button>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          {line.day_type?.replace('_', ' ')} · paid at {Math.round(percent * 100)}%.
          Uncheck any meal that was provided — it comes off in full before the percentage applies.
        </p>

        <div className="flex gap-2">
          {([
            ['breakfastClaimed', 'Breakfast', components.breakfast],
            ['lunchClaimed', 'Lunch', components.lunch],
            ['dinnerClaimed', 'Dinner', components.dinner],
          ] as const).map(([key, label, value]) => (
            <button
              key={key}
              type="button"
              aria-pressed={claims[key]}
              onClick={() => setClaims({ ...claims, [key]: !claims[key] })}
              className={`min-h-16 flex-1 rounded-lg border px-2 text-sm font-medium ${
                claims[key]
                  ? 'border-brand bg-brand-light text-brand'
                  : 'border-slate-300 bg-white text-slate-400 line-through'
              }`}
            >
              {label}
              <span className="mt-0.5 block text-xs font-normal">${value.toFixed(2)}</span>
            </button>
          ))}
        </div>

        <div>
          <label className="label" htmlFor="pdnote">Why (optional)</label>
          <input id="pdnote" className="field" value={note} placeholder="Lunch provided at the show"
                 onChange={(e) => setNote(e.target.value)} />
        </div>

        {confirmingDelete ? (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            Remove this day from the report? Regenerating per diem will bring it back.
          </p>
        ) : null}

        <ErrorNote>{error}</ErrorNote>
      </div>
    </Sheet>
  );
}
